import { Button } from "@shopify/polaris"
import {
	usePostHog,
	useFeatureFlagEnabled,
	useFeatureFlagVariantKey,
} from "posthog-js/react"
import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { CoolButton } from "../components/CoolButton.jsx"
import { LanguageSelector } from "../components/LanguageSelector.jsx"
import { OnboardingTimeline } from "../components/OnboardingTimeline.jsx"
import { Survey } from "../components/Survey.jsx"
import { TopMenu } from "../components/TopMenu.jsx"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider.jsx"
import { BillingBundles } from "../components/subscription/BillingBundles.jsx"
import { BillingItems } from "../components/subscription/BillingItems.jsx"
import { BillingPeriodSelector } from "../components/subscription/BillingPeriodSelector.jsx"
import { useElementDimensions } from "../hooks/useElementDimensions.js"
import { useMediaQuery } from "../hooks/useMediaQuery.js"
import {
	useAdditionalStorePricingVariant,
	useCheaperAdditionalStoresTest,
	useFeatureFlagsReady,
	useGetItemPriceId,
} from "../hooks/usePricingHooks.js"
import { useTrackFacebookEvent } from "../hooks/useTrackFacebookEvent.js"
import { getFbcCookie, getFbpCookie } from "../utils/getFbcCookie.js"
import { getIpAddress } from "../utils/getIpAddress.js"
import {
	captureMetaAdsEvent,
	META_ADS_EVENTS,
} from "../utils/posthogMetaAds.js"

const Subscription = ({
	newUser = false,
	useNewUserDiscounts = false,
	showOnlyEssentialPlan = false,
}) => {
	const posthog = usePostHog()
	const variant = useFeatureFlagVariantKey(
		"no-customize-plan-for-paid-traffic",
	)
	const { state, dispatch } = useContext(GlobalStateContext)
	const { t } = useTranslation()
	const navigate = useNavigate()
	const trackFacebookEvent = useTrackFacebookEvent()
	const getItemPriceId = useGetItemPriceId()
	const additionalStorePricingVariant = useAdditionalStorePricingVariant()
	const {
		active: cheaperAdditionalStoresTestActive,
		savings: cheaperAdditionalStoresSavings,
	} = useCheaperAdditionalStoresTest()
	// Additional-store users never get the à-la-carte customize option
	// (prod hides it via the no-customize flag; this also covers dev and
	// the starter-only test arm).
	const hideCustomizePlan =
		variant === "no-customize" || additionalStorePricingVariant != null
	const pricingReady = useFeatureFlagsReady()
	const isMobile = useMediaQuery("(max-width: 700px)")
	const { right: containerRight, ref: containerRef } = useElementDimensions()

	const [showBundles, setShowBundles] = useState(true)
	const [showCustom, setShowCustom] = useState(false)
	const [cart, setCart] = useState([])
	const [billingPeriod, setBillingPeriod] = useState("yearly")

	// Only allow discounts for cart items if Basic and Plus are also in the cart
	const disableDiscountsForCartItems =
		showCustom && (!cart.includes("basic") || !cart.includes("plus"))

	const toggleCustom = (showCustom) => {
		setCart([])

		if (showCustom) {
			setShowBundles(false)
			setTimeout(() => {
				setShowCustom(true)
			}, 900)
		} else {
			setShowCustom(false)
			setTimeout(() => {
				setShowBundles(true)
			}, 700)
		}
	}

	const toggleFromCart = (item) => {
		let addKopyBasic = false
		if (item === "plus") {
			addKopyBasic = true
		}

		const isInCartAlready = cart.includes(item)
		const kopyBasicInCart = cart.includes("basic")

		if (isInCartAlready) {
			setCart(cart.filter((i) => i !== item))
		} else {
			setCart([
				...cart,
				item,
				...(addKopyBasic && !kopyBasicInCart ? ["basic"] : []),
			])
		}
	}

	const onBundleSelected = (bundleSelected) => {
		openCheckout({ bundleSelected })
	}

	const openCheckout = async ({ cartItems = [], bundleSelected = null }) => {
		if (!pricingReady) return

		let checkoutItems = []

		// Additional-store pricing experiment: only relevant when Kopy Basic is
		// part of this checkout for an additional-store user.
		const basicInCheckout =
			cartItems?.includes("basic") ||
			(typeof bundleSelected === "string" &&
				bundleSelected.includes("basic"))
		const experimentVariant =
			additionalStorePricingVariant && basicInCheckout
				? additionalStorePricingVariant
				: null

		if (cartItems?.length) {
			cartItems.map((item) => {
				checkoutItems.push(getItemPriceId(item, billingPeriod))
			})
		} else if (bundleSelected) {
			if (bundleSelected === "basic") {
				checkoutItems.push(getItemPriceId("basic", billingPeriod))
			} else if (bundleSelected === "basic_plus") {
				checkoutItems.push(getItemPriceId("basic", billingPeriod))
				checkoutItems.push(getItemPriceId("plus", billingPeriod))
			} else if (bundleSelected === "basic_plus_protect") {
				checkoutItems.push(getItemPriceId("basic", billingPeriod))
				checkoutItems.push(getItemPriceId("plus", billingPeriod))
				checkoutItems.push(getItemPriceId("protect", billingPeriod))
			} else if (bundleSelected === "basic_protect") {
				checkoutItems.push(getItemPriceId("basic", billingPeriod))
				checkoutItems.push(getItemPriceId("protect", billingPeriod))
			} else if (bundleSelected === "protect") {
				checkoutItems.push(getItemPriceId("protect", billingPeriod))
			}
		}

		checkoutItems = checkoutItems
			.filter((item) => item !== null)
			.map((item) => ({ priceId: item, quantity: 1 }))

		/* prettier-ignore */
		const discountId =
			useNewUserDiscounts ?
				cartItems?.length > 0 ? // Only apply discount if at least Basic and Plus are selected, regardless of Protect being selected
					!disableDiscountsForCartItems ?
						state.discounts?.essential_new_users?.[billingPeriod]?.discountId
					: null
				: state.discounts?.essential_new_users?.[billingPeriod]?.discountId
			: null

		const fbc = getFbcCookie()
		const fbp = getFbpCookie()
		const clientUserAgent = navigator.userAgent || null
		const ipAddress = await getIpAddress()

		posthog?.capture("user_opens_checkout_fbc_testing", {
			userId: state.userId,
			shop: state.shop,
			userEmail: state.userEmail,
			fbc,
			fbp,
			clientUserAgent,
			ipAddress,
		})

		posthog?.capture("user_opens_paddle_checkout_v2", {
			cart,
			bundleSelected,
			billingPeriod,
			// total,
		})

		if (experimentVariant) {
			posthog?.capture("additional_store_pricing_checkout_opened", {
				variant: experimentVariant,
				billingPeriod,
				store: state.shop,
			})
		}

		window.Paddle.Checkout.open({
			settings: {
				allowLogout: false,
				successUrl: `${window.location.origin}/${hasRightToTrial ? "?subbed_trial" : ""}`,
			},
			customer: state.paddleCustomerId
				? { id: state.paddleCustomerId }
				: { email: state.userEmail },
			items: checkoutItems,
			customData: {
				store: state.shop,
				tolt_referral: window.tolt_referral || null,
				_fbc: fbc || null,
				_fbp: fbp || null,
				client_user_agent: clientUserAgent || null,
				ip_address: ipAddress || null,
				subscriptionVersion: "v2",
				isTrialSubscriptionFromEmailOffer:
					state.hasNotTrialedFromEmailOfferAndUrlIsFromEmailOffer,
				traffic_source: localStorage.getItem("referrer") || null,
				gtm_user_id: localStorage.getItem("gtm_user_id") || null,
				customer_email: state.userEmail || null,
				...(experimentVariant
					? { additionalStorePricingVariant: experimentVariant }
					: {}),
			},
			...(discountId ? { discountId } : {}),
			customerAuthToken: state.customerAuthToken,
		})
	}

	useEffect(() => {
		if (
			state.hasValidKopyBasicSubscription ||
			state.hasValidKopyProtectSubscription
		) {
			navigate("/")
		} else {
			posthog?.capture("user_visits_subscription_page_v2")
			captureMetaAdsEvent(posthog, META_ADS_EVENTS.VIEW_CONTENT)
			trackFacebookEvent("ViewContent")
		}
	}, [])

	const hasAnotherStoreLinked = Object.keys(state.stores).length > 0
	const hasTrialedKopyBasicOrProtectBefore =
		state.hasTrialedKopyBasicBefore || state.hasTrialedKopyProtectBefore
	const hasRightToTrial =
		!hasTrialedKopyBasicOrProtectBefore ||
		state.hasNotTrialedFromEmailOfferAndUrlIsFromEmailOffer

	return (
		<div
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				padding: "0rem 1rem",
			}}
		>
			<TopMenu accountSelector storeSelector={hasAnotherStoreLinked} />

			<div
				style={{
					display: "flex",
					gap: "1rem",
					justifyContent: "center",
					position: "relative",
					width: "100%",
				}}
			>
				<div
					className="border shadow"
					ref={containerRef}
					style={{
						marginTop: "5rem",
						marginBottom: "5rem",
						maxWidth: isMobile ? "100%" : "680px",
						padding: "1rem",
						background: "#fff",
						borderRadius: "2rem",
						flex: "1",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							flexDirection: isMobile ? "column" : "row",
							marginBottom: isMobile ? "2rem" : "0rem",
						}}
					>
						<img
							style={{
								display: "block",
								height: "7rem",
								width: "7rem",
								objectFit: "cover",
							}}
							src="https://kopy.b-cdn.net/cart1.webp"
							srcSet="https://kopy.b-cdn.net/cart2.webp 2x"
						/>

						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "start",
								gap: "0.5rem",
							}}
						>
							<h1
								style={{
									fontSize: "2.5rem",
									fontWeight: "700",
									color: "#222",
									lineHeight: "1",
								}}
							>
								{hasRightToTrial
									? t(
											"subscription.start_your_3-day_free_trial",
										)
									: t("subscription.subscription_plans")}
							</h1>
							<p
								style={{
									fontSize: "1rem",
									fontWeight: "500",
									color: "#777",
									lineHeight: "1",
								}}
							>
								{hasRightToTrial
									? t(
											"subscription.try_100_free_for_3_days_cancel_anytime",
										)
									: t(
											"subscription.choose_the_best_plan_for_you",
										)}
							</p>
						</div>
					</div>

					<div
						style={{
							marginTop: "1rem",
							marginBottom: "1rem",
							overflow: "hidden",
						}}
					>
						{state.hasValidKopyPlusSubscription && (
							<div
								style={{
									border: "1px solid #eee",
									background: "#fafafa",
									borderRadius: "1rem",
									padding: "1rem",
									marginBottom: "1rem",
									marginLeft: "auto",
									marginRight: "auto",
									width: "100%",
								}}
							>
								<p
									style={{
										fontSize: "1rem",
										color: "#333",
										fontWeight: "500",
										marginBottom: "0.5rem",
										lineHeight: "1.2",
										textAlign: "center",
									}}
								>
									💡{" "}
									{t(
										"components.billing_bundles.you_already_have_a_kopy_plus_subscription_in_one_of_your_other_stores",
									)}
								</p>
								<p
									style={{
										fontSize: "0.75rem",
										color: "#777",
										lineHeight: "1.2",
									}}
								>
									{t(
										"components.billing_bundles.the_benefits_of_kopy_plus_are_applied_account-wide_to_all_of_your_stores_since_you_already_have_one",
									)}
								</p>
							</div>
						)}

						<div
							style={{
								display: "flex",
								justifyContent: "center",
								width: "100%",
								paddingTop: "2rem",
							}}
						>
							<BillingPeriodSelector
								showDiscounts={useNewUserDiscounts}
								billingPeriod={billingPeriod}
								setBillingPeriod={setBillingPeriod}
								disableDiscountsForCartItems={
									disableDiscountsForCartItems
								}
								additionalSavings={
									cheaperAdditionalStoresTestActive
										? cheaperAdditionalStoresSavings
										: null
								}
							/>
						</div>

						<div
							className={`bundles bundles-${showBundles ? "show" : "hide"}`}
							style={{ marginTop: "2rem" }}
						>
							<BillingBundles
								onBundleSelected={onBundleSelected}
								hasRightToTrial={hasRightToTrial}
								billingPeriod={billingPeriod}
								useNewUserDiscounts={useNewUserDiscounts}
								showOnlyEssentialPlan={showOnlyEssentialPlan}
								showOnlyStarter={cheaperAdditionalStoresTestActive}
							/>
						</div>

						<div
							className={`custom custom-${showCustom ? "show" : "hide"}`}
						>
							<BillingItems
								cart={cart}
								toggleFromCart={toggleFromCart}
							/>
						</div>
					</div>

					{cart.length > 0 ? (
						<div
							style={{
								marginTop: "2.8rem",
								marginBottom: "2rem",
								display: "flex",
								justifyContent: "center",
							}}
						>
							<CoolButton
								onClick={() =>
									openCheckout({ cartItems: cart })
								}
								disabled={!pricingReady}
							>
								<span
									style={{
										fontSize: "1rem",
										fontWeight: "500",
										color: "#222",
										padding: "0rem 1rem",
									}}
								>
									{hasRightToTrial
										? t(
												"subscription.start_your_3-day_free_trial",
											)
										: t("subscription.buy_now")}
								</span>
							</CoolButton>
						</div>
					) : !hideCustomizePlan ? (
						<div
							style={{
								marginTop: "1rem",
								marginBottom: "2rem",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: "1rem",
							}}
						>
							<div
								style={{
									fontSize: "1rem",
									fontWeight: "600",
									color: "#222",
								}}
							>
								{t("common.or")}
							</div>
							<Button onClick={() => toggleCustom(showBundles)}>
								{showBundles
									? t("subscription.customize_your_plan")
									: t("subscription.show_bundles")}
							</Button>
						</div>
					) : null}
				</div>

				<div
					className="float-right"
					style={{
						left: `calc(${containerRight}px + 0.2rem)`,
						top: "7rem",
						paddingRight: "1rem",
					}}
				>
					<OnboardingTimeline
						step={newUser ? 1 : 2}
						newUser={newUser}
					/>
					<style>{`
						.float-right {
							position: absolute;
							width: max-content;
						}

						@media (max-width: 900px) {
							.float-right {
								display: none;
							}
						}
						@media (max-width: 1200px) {
							.float-right {
								width: fit-content;
							}
						}
					`}</style>
				</div>
			</div>

			<div
				style={{
					position: "fixed",
					bottom: "2rem",
					left: "2rem",
					zIndex: "500",
				}}
			>
				<LanguageSelector />
			</div>

			<Survey />

			<style>{`
				.bundles {
					transition: max-height 1s ease, opacity 1s ease;
				}
				.bundles-show {
					pointer-events: all;
					max-height: 150rem;
					opacity: 1;
				}
				.bundles-hide {
					pointer-events: none;
					max-height: 0;
					opacity: 0;
				}

				.custom {
					transition: max-height 1s ease, opacity 1s ease;
				}
				.custom-show {
					pointer-events: all;
					max-height: 100rem;
					opacity: 1;
				}
				.custom-hide {
					pointer-events: none;
					max-height: 0;
					opacity: 0;
				}
			}`}</style>
		</div>
	)
}

export default Subscription
